import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const THRESHOLD_OPTS = [30, 14, 7, 1];

export default function SettingsDialog({ open, onClose, settings, onSaved }) {
  const [email, setEmail] = useState("");
  const [thresholds, setThresholds] = useState([30, 14, 7, 1]);
  const [enabled, setEnabled] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setEmail(settings?.reminder_email || "");
      setThresholds(settings?.thresholds?.length ? settings.thresholds : [30, 14, 7, 1]);
      setEnabled(!!settings?.enabled);
    }
  }, [open, settings]);

  const toggle = (t) =>
    setThresholds((cur) => (cur.includes(t) ? cur.filter((x) => x !== t) : [...cur, t]));

  const save = async () => {
    if (thresholds.length === 0) {
      toast.error("Keep at least one alert checkpoint");
      return;
    }
    setBusy(true);
    try {
      await api.put("/settings", { reminder_email: email, thresholds, enabled });
      toast.success("Reminder settings saved");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-panel border-line text-hi max-w-md" data-testid="settings-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-black uppercase tracking-wide">Email reminders</DialogTitle>
          <DialogDescription className="text-mid text-sm">
            One digest email before anything expires — at the checkpoints you pick.
          </DialogDescription>
        </DialogHeader>
        <div className="mt-2 space-y-5">
          <div className="flex items-center justify-between rounded-xl border border-line bg-ink/60 px-4 py-3">
            <div>
              <p className="text-sm font-bold">Reminders on</p>
              <p className="text-xs text-mid">Checks the fleet every few hours</p>
            </div>
            <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="email-reminder-toggle" />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="set-email" className="font-mono text-[10px] uppercase tracking-[0.2em] text-mid">Send reminders to</Label>
            <Input
              id="set-email"
              data-testid="reminder-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@garage.co.uk"
            />
          </div>

          <div className="space-y-2">
            <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-mid">Alert checkpoints</Label>
            <div className="grid grid-cols-2 gap-2">
              {THRESHOLD_OPTS.map((t) => (
                <label key={t} className="flex items-center gap-3 rounded-xl border border-line bg-ink/60 px-4 py-3 cursor-pointer hover:border-linehi transition">
                  <Checkbox checked={thresholds.includes(t)} onCheckedChange={() => toggle(t)} data-testid={`threshold-${t}-checkbox`} />
                  <span className="text-sm">{t} {t === 1 ? "day" : "days"} before</span>
                </label>
              ))}
            </div>
            <p className="text-xs text-low">A due-day alert is always sent on the day itself.</p>
          </div>

          <button
            onClick={save}
            data-testid="save-settings-button"
            disabled={busy}
            className="w-full rounded-full bg-ember py-3 font-display text-lg font-bold uppercase tracking-wider text-ink hover:bg-ember/90 disabled:opacity-40 transition"
          >
            {busy ? "Saving…" : "Save settings"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
